
import { useState, useCallback, useContext } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toast } from '@/components/ui/toast';
import { apiCall } from '@/lib/api/api-helpers';
import { NodeFileUploadContext } from '@/components/studio/node-editor/file-upload-context';

// Canvas File interface (aligned with API response)
interface CanvasFile {
  uuid: string;
  filename: string;
  contentType: string;
  size: number;
  publicUrl: string | null;
  nodeId: string | null;
  status: 'ACTIVE' | 'PROCESSING' | 'FAILED' | 'DELETED';
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt?: string;
}

// Upload input
interface FileUploadInput {
  file: File;
  nodeId?: string;
  metadata?: Record<string, any>;
}

// Update input
interface FileUpdateInput {
  filename?: string;
  nodeId?: string | null;
  metadata?: Record<string, any>;
  status?: 'ACTIVE' | 'PROCESSING' | 'FAILED' | 'DELETED';
}

// List query options
interface FileListQuery {
  nodeId?: string;
  status?: 'ACTIVE' | 'PROCESSING' | 'FAILED' | 'DELETED';
  contentType?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'filename' | 'size';
  sortOrder?: 'asc' | 'desc';
}

// AI Image Processing Input
interface AIImageProcessInput {
  imageUrl: string;
  nodeId?: string;
}

// Hook return type
interface UseCanvasFilesReturn {
  files: CanvasFile[];
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;
  uploadProgress: number | null;
  uploadFile: (canvasId: string, input: FileUploadInput) => Promise<CanvasFile | null>;
  processAIImage: (canvasId: string, input: AIImageProcessInput) => Promise<CanvasFile | null>;
  listFiles: (canvasId: string, query?: FileListQuery) => Promise<CanvasFile[]>;
  getFile: (canvasId: string, fileUuid: string) => Promise<CanvasFile | null>;
  updateFile: (canvasId: string, fileUuid: string, input: FileUpdateInput) => Promise<CanvasFile | null>;
  deleteFile: (canvasId: string, fileUuid: string, permanent?: boolean) => Promise<boolean>;
}

export function useCanvasFiles(): UseCanvasFilesReturn {
  const [files, setFiles] = useState<CanvasFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const analytics = useAnalytics();
  const fileUploadHost = useContext(NodeFileUploadContext);

  // Wrapper around shared apiCall; falls back to raw fetch for FormData (multipart)
  const callApi = useCallback(
    async (endpoint: string, options: RequestInit = {}) => {
      if (options.body instanceof FormData) {
        // Multipart: raw fetch — shared apiCall sets Content-Type: application/json which breaks FormData
        if (!isAuthenticated) throw new Error('Not authenticated');
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
        if (!apiBaseUrl) throw new Error('VITE_API_BASE_URL is not configured');
        const response = await fetch(`${apiBaseUrl}${endpoint}`, {
          ...options,
          credentials: 'include',
          headers: { ...options.headers },
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error((errorData as { error?: string }).error || `HTTP ${response.status}`);
        }
        return response.json();
      }
      return apiCall(endpoint, isAuthenticated, options);
    },
    [isAuthenticated]
  );

  // Upload file with retry logic
  const uploadFile = useCallback(
    async (canvasId: string, input: FileUploadInput, maxRetries = 3): Promise<CanvasFile | null> => {
      try {
        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            if (fileUploadHost) {
              const stored = await fileUploadHost.uploadFile(canvasId, input.file);
              const uploaded: CanvasFile = {
                uuid: stored.id,
                filename: input.file.name,
                contentType: stored.contentType,
                size: input.file.size,
                publicUrl: stored.url,
                nodeId: input.nodeId ?? null,
                status: 'ACTIVE',
                metadata: input.metadata,
                createdAt: new Date().toISOString(),
              };
              setUploadProgress(100);
              setFiles(prev => [...prev, uploaded]);
              window.dispatchEvent(new CustomEvent('genie:file-created', { detail: uploaded }));
              return uploaded;
            }

            // Create FormData
            const formData = new FormData();
            formData.append('file', input.file);
            if (input.nodeId) {
              formData.append('nodeId', input.nodeId);
            }
            if (input.metadata) {
              formData.append('metadata', JSON.stringify(input.metadata));
            }

            // Upload with progress tracking
            const response = await callApi(`/api/canvas/${canvasId}/files`, {
              method: 'POST',
              body: formData,
            });

            setUploadProgress(100);

            if (response.success && response.data) {
              // Add to files list
              setFiles((prev) => [...prev, response.data]);

              // Notify Files Panel to refresh
              window.dispatchEvent(new CustomEvent('genie:file-created', { detail: response.data }));

              // Success toast
              if (attempt > 1) {
                toast.success(`Upload successful after ${attempt} attempts`);
              }

              return response.data;
            } else {
              throw new Error(response.error || 'Failed to upload file');
            }
          } catch (err) {
            console.error(`Upload attempt ${attempt}/${maxRetries} failed:`, err);

            // Last attempt failed
            if (attempt === maxRetries) {
              const errorMessage = err instanceof Error ? err.message : 'Failed to upload file';
              setError(errorMessage);

              // Toast notification
              toast.error(`Upload failed: ${errorMessage}`);

              // Analytics error tracking
              await analytics.captureException(err instanceof Error ? err : new Error(errorMessage), {
                operation: 'file_upload',
                canvasId,
                filename: input.file.name,
                fileSize: input.file.size,
                attempts: maxRetries,
              });

              return null;
            }

            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, attempt - 1) * 1000;
            toast(`Retrying upload (${attempt}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }

        return null;
      } finally {
        // CRITICAL: Always clear uploading state, regardless of success or failure
        setIsUploading(false);
        setUploadProgress(null);
      }
    },
    [callApi, analytics, fileUploadHost]
  );

  // List files
  const listFiles = useCallback(
    async (canvasId: string, query?: FileListQuery): Promise<CanvasFile[]> => {
      try {
        setIsLoading(true);
        setError(null);

        // Build query string
        const params = new URLSearchParams();
        if (query?.nodeId) params.append('nodeId', query.nodeId);
        if (query?.status) params.append('status', query.status);
        if (query?.contentType) params.append('contentType', query.contentType);
        if (query?.limit) params.append('limit', query.limit.toString());
        if (query?.offset) params.append('offset', query.offset.toString());
        if (query?.sortBy) params.append('sortBy', query.sortBy);
        if (query?.sortOrder) params.append('sortOrder', query.sortOrder);

        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await callApi(`/api/canvas/${canvasId}/files${queryString}`);

        if (response.success && response.data) {
          setFiles(response.data);
          return response.data;
        } else {
          throw new Error(response.error || 'Failed to list files');
        }
      } catch (err) {
        console.error('Failed to list files:', err);
        setError(err instanceof Error ? err.message : 'Failed to list files');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [callApi]
  );

  // Get single file
  const getFile = useCallback(
    async (canvasId: string, fileUuid: string): Promise<CanvasFile | null> => {
      try {
        setError(null);

        const response = await callApi(`/api/canvas/${canvasId}/files/${fileUuid}`);

        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error(response.error || 'Failed to get file');
        }
      } catch (err) {
        console.error('Failed to get file:', err);
        setError(err instanceof Error ? err.message : 'Failed to get file');
        return null;
      }
    },
    [callApi]
  );

  // Update file metadata
  const updateFile = useCallback(
    async (canvasId: string, fileUuid: string, input: FileUpdateInput): Promise<CanvasFile | null> => {
      try {
        setError(null);

        const response = await callApi(`/api/canvas/${canvasId}/files/${fileUuid}`, {
          method: 'PUT',
          body: JSON.stringify(input),
        });

        if (response.success && response.data) {
          // Update local state
          setFiles((prev) => prev.map((file) => (file.uuid === fileUuid ? response.data : file)));
          return response.data;
        } else {
          throw new Error(response.error || 'Failed to update file');
        }
      } catch (err) {
        console.error('Failed to update file:', err);
        setError(err instanceof Error ? err.message : 'Failed to update file');
        return null;
      }
    },
    [callApi]
  );

  // Process AI-generated image from URL (fetch + 3 versions + storage)
  const processAIImage = useCallback(
    async (canvasId: string, input: AIImageProcessInput): Promise<CanvasFile | null> => {
      try {
        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        // Call backend endpoint to process AI image
        const response = await callApi(`/api/canvas/${canvasId}/files/from-url`, {
          method: 'POST',
          body: JSON.stringify(input),
        });

        setUploadProgress(100);

        if (response.success && response.data) {
          // Add to files list
          setFiles((prev) => [...prev, response.data]);
          return response.data;
        } else {
          throw new Error(response.error || 'Failed to process AI image');
        }
      } catch (err) {
        console.error('Failed to process AI image:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to process AI image';
        setError(errorMessage);

        // Toast notification
        toast.error(`AI image processing failed: ${errorMessage}`);

        // Analytics error tracking
        await analytics.captureException(err instanceof Error ? err : new Error(errorMessage), {
          operation: 'ai_image_processing',
          canvasId,
          nodeId: input.nodeId,
          imageUrl: input.imageUrl,
        });

        throw err;
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
      }
    },
    [callApi, analytics]
  );

  // Delete file
  const deleteFile = useCallback(
    async (canvasId: string, fileUuid: string, permanent = false): Promise<boolean> => {
      try {
        setError(null);

        const queryParam = permanent ? '?permanent=true' : '';
        const response = await callApi(`/api/canvas/${canvasId}/files/${fileUuid}${queryParam}`, {
          method: 'DELETE',
        });

        if (response.success) {
          // Remove from local state
          setFiles((prev) => prev.filter((file) => file.uuid !== fileUuid));
          return true;
        } else {
          throw new Error(response.error || 'Failed to delete file');
        }
      } catch (err) {
        console.error('Failed to delete file:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete file');
        return false;
      }
    },
    [callApi]
  );

  return {
    files,
    isLoading,
    isUploading,
    error,
    uploadProgress,
    uploadFile,
    processAIImage,
    listFiles,
    getFile,
    updateFile,
    deleteFile,
  };
}

// Export types for use in components
export type { CanvasFile, FileUploadInput, FileUpdateInput, FileListQuery, AIImageProcessInput };
